import { useEffect, useRef } from "react";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { useAuth } from "@/lib/contexts/AuthContext";
import { track } from "@/lib/utils/track";
import {
  isTrialActivatingFresh,
  clearTrialActivating,
} from "@/lib/utils/trialActivation";

/**
 * Null-render component — mounted once inside <Router> in App.tsx.
 *
 * Polls refreshSubscription() every 3s while the trial-activation flag is fresh
 * and the account still reads as free. Fires a `trial_activated` tracking event
 * once the subscription resolves, then clears the flag and stops polling.
 * Single mount guarantees one poll loop regardless of the active route.
 */
export function TrialActivationWatcher() {
  const { hasActiveSubscription, hasActiveTrial, isFreePlan, refreshSubscription } =
    useSubscription();
  const { token } = useAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const trackedRef = useRef(false);

  useEffect(() => {
    const resolved = hasActiveSubscription || hasActiveTrial || !isFreePlan;

    if (resolved) {
      if (!trackedRef.current && isTrialActivatingFresh()) {
        trackedRef.current = true;
        track("trial_activated", token, { activation_method: "stripe_watcher" });
      }
      clearTrialActivating();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (!isTrialActivatingFresh()) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        if (!isTrialActivatingFresh()) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          return;
        }
        refreshSubscription();
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hasActiveSubscription, hasActiveTrial, isFreePlan, refreshSubscription, token]);

  return null;
}
